/** @file Hook: rAF paint loop of the curve graph (dirty flag), device-pixel sizing and wheel zoom. */
import { useEffect, useRef, type RefObject } from 'react';
import { playheadStore } from '../../stores/playheadStore';
import { activeCurveKey } from './activeCurveKey';
import type { CurveStyle } from './curveCanvasTypes';
import type { CurvePointerContext } from './curvePointer';
import { curvePrepCache } from './curvePrepCache';
import { curveStyleFrom } from './curveStyleFrom';
import { curveViewStore } from './curveViewStore';
import { curveWheelAxes } from './curveWheelAxes';
import { drawCurveGraph } from './drawCurveGraph';
import type { prepareCurveFrame } from './prepareCurveFrame';
import type { CurveModel } from './useCurveModel';

/**
 * Sizes the backing store to the device pixel ratio (only when it changed: a resize reallocates and clears it),
 * paints, and returns what the pointer machine picks against. `prepare` is cached, so a playhead move only redraws.
 */
function paint(canvas: HTMLCanvasElement, model: CurveModel, style: CurveStyle, prepare: typeof prepareCurveFrame): CurvePointerContext | null {
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;
  const size = { width: canvas.clientWidth, height: canvas.clientHeight };
  const dpr = window.devicePixelRatio || 1;
  const [w, h] = [Math.round(size.width * dpr), Math.round(size.height * dpr)];
  if (canvas.width !== w || canvas.height !== h) [canvas.width, canvas.height] = [w, h];
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const view = curveViewStore.getState();
  const prep = prepare(model.tracks, model.keys, view, size);
  if (prep.fitted && !view.fitted) curveViewStore.setState({ ...prep.axes, fitted: true });
  const box = view.gesture.kind === 'box' ? view.gesture : null;
  drawCurveGraph(ctx, { ...size, scene: prep.scene, axes: prep.axes, playhead: playheadStore.getState().frame, activeTrack: activeCurveKey(model.tracks, view.active)?.track ?? -1, box }, style);
  return { tracks: model.tracks, scene: prep.scene, grid: prep.grid, maps: prep.maps, ...prep.axes };
}

/** Repaints on the next animation frame after the model, the view store, the playhead or the size changed. */
export function useCurveCanvas(canvasRef: RefObject<HTMLCanvasElement | null>, model: CurveModel): RefObject<CurvePointerContext | null> {
  const last = useRef<CurvePointerContext | null>(null);
  const modelRef = useRef(model);
  const dirty = useRef(true);
  useEffect(() => {
    modelRef.current = model;
    dirty.current = true;
  }, [model]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return undefined;
    const style = curveStyleFrom((name) => getComputedStyle(canvas).getPropertyValue(name));
    const prepare = curvePrepCache();
    const mark = (): void => { dirty.current = true; };
    const stops = [curveViewStore.subscribe(mark), playheadStore.subscribe(mark)];
    const observer = new ResizeObserver(mark);
    observer.observe(canvas);
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      curveViewStore.setState(curveWheelAxes(curveViewStore.getState(), { x: e.clientX - r.left, y: e.clientY - r.top, deltaY: e.deltaY, ctrl: e.ctrlKey, shift: e.shiftKey }));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    let raf = 0;
    const loop = (): void => {
      if (dirty.current) {
        dirty.current = false;
        last.current = paint(canvas, modelRef.current, style, prepare);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener('wheel', onWheel);
      observer.disconnect();
      for (const stop of stops) stop();
    };
  }, [canvasRef]);
  return last;
}
